import React, { useEffect, useMemo, useState, useRef } from 'react'
import { DrawerContainer } from '../../components/DrawerContainer'
import { Box, Typography, Stack, useTheme, Button, Collapse } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { InvestigationBlockInfo } from '../../components/InvestigationBlockInfo'
import { DetailsTodo, InvestigationCollapsable, Separator } from '../../components/InvestigationComponents'
import { EGBasicSelect } from '../../../../../MainComponents/Select/EGBasicSelect'
import Icon from '../../../../../Icon/Icon'
import _ from 'lodash'
import { InvestigationButton } from '../../components/InvestigationButton'
import { EGButton } from '../../../../../MainComponents/Button/EGButton/EGButton'
import { useSelector } from 'react-redux'
import { formatDateAndTime } from '../../helpers/formatDate'
import { todoSchema } from '../schemas'
import { useConstants } from '../../Main/useConstants'

export const ActivityInfo = ({
  open,
  onClose,
  selectedActivity,
  setSelectedActivity,
  plans,
  handleSavePlans,
  setIsEditing,
}) => {
    
  const [t] = useTranslation("investigation");
  const theme = useTheme();
  const deleteConfirmationRef = useRef(null);

  const jaguarState = useSelector((state) => state.jaguar);

  const { enums, functions } = useConstants();

  const [activity, setActivity] = useState(plans[selectedActivity] || null);
  const [isSavingTodos, setIsSavingTodos] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const dateDeadline = useMemo(() => 
    formatDateAndTime(activity?.deadline, activity?.timeDeadline, t("activityPlan.not_scheduled")),
    [activity?.deadline, activity?.timeDeadline]
  );

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        resetValues();
      }, 200);
    }
  }, [open]);

  useEffect(() => {
    if (selectedActivity !== null) {
      setActivity(plans[selectedActivity]);
    }
  }, [selectedActivity, plans]);

  useEffect(() => {
    if (isDeleting && deleteConfirmationRef.current) {
      deleteConfirmationRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isDeleting]);

  const handleToggleTodos = async(idxTodo, todo, onFinish) => {
    setIsSavingTodos(true);
    const newPlans = _.cloneDeep(plans);
    newPlans[selectedActivity].todos[idxTodo] = todo;
    
    try {
      await handleSavePlans(newPlans, selectedActivity);
    } finally {
      onFinish?.();
      setIsSavingTodos(false);
    }
  }

  const handleEdit = () => {
    setIsEditing(true);
    onClose();
  }

  const handleDelete = async() => {
    const newPlans = _.cloneDeep(plans);
    newPlans.splice(selectedActivity, 1);
    await handleSavePlans(newPlans);
    onClose();
  }

  const resetValues = () => {
    setSelectedActivity(null);
    setIsSavingTodos(false);
    setIsDeleting(false);
  }

  return (
    <DrawerContainer open={open} onClose={onClose}>
      <Box padding="15px 20px 20px 20px">
        <Typography 
          fontSize={28}
          fontWeight={700}
          color="primary" 
          lineHeight={1}
        >
          {t("Nueva")}
        </Typography>

        <Box
          sx={{
            mt: 1.5,
            padding: 2,
            border: `1px solid ${theme.palette.blueGray200.main}`,
            borderRadius: 2,
          }}
        >

          <Typography color="blueGray.main" fontSize={14} fontWeight={600}>
            {activity?.name}
          </Typography>
          
          <div className="hr-div" >.</div>

          <Stack spacing={2}>
            {
              activity?.assignedTo?.id && activity?.assignedTo?.from &&
              <InvestigationBlockInfo
                label={t("activityPlan.responsible_of_the_activity")}
                labelColor={theme.palette.blueGray.main}
                text={functions.getUserLabel(activity?.assignedTo)}
                size={12}
              />
            }

            {
              activity?.details &&
              <InvestigationBlockInfo 
                label={t("activityPlan.details_of_the_activity")}
                labelColor={theme.palette.blueGray.main}
                text={activity?.details ?? t("activityPlan.no_details")}
                size={12}
              />
            }

            {
              activity?.party?.length > 0 &&
              <InvestigationBlockInfo 
                label={t("activityPlan.participants")}
                labelColor={theme.palette.blueGray.main}
                text={activity?.party?.map(item => functions.getUserLabel(item)).join(", ")}
                size={12}
              />
            }

            {
              activity?.deadline &&
              <InvestigationBlockInfo 
                label={t("activityPlan.deadline")}
                labelColor={theme.palette.blueGray.main}
                text={dateDeadline}
                size={12}
              />
            }

          </Stack>

          {
            activity?.resources?.length > 0 &&
            <>
              <Separator  text={t("activityPlan.assigned_resources")} sx={{ my: 2 }} />

              <Stack spacing={2} >
                {
                  activity?.resources?.map((resource, idx) => (
                    <Resource key={idx} resource={resource} />
                  ))
                }
              </Stack>
            </>
          }

          {
            activity?.todos?.length > 0 &&
            <>
              <Separator  text={t("activityPlan.assigned_tasks")} sx={{ my: 2 }} />

              <Stack spacing={2} >
                {
                  activity?.todos?.map((todo, idx) => (
                    <Todo 
                      key={idx} 
                      todo={todo}
                      idxTodo={idx}
                      isSavingTodos={isSavingTodos}
                      handleToggleTodos={handleToggleTodos}
                    />
                  ))
                }
              </Stack>
            </>
          }

          <Box display="flex" gap={1} mt={2} justifyContent="flex-end">
            <InvestigationButton
              onClick={() => setIsDeleting(true)}
              icon={"trash_can"} 
              iconColor={isDeleting ? theme.palette.blueGray200.main : theme.palette.red.main}
              disabled={isDeleting}
              size="thin"
            />
            <InvestigationButton
              label={t("activityPlan.edit")} 
              icon={"outline_edit"} 
              iconColor={isDeleting ? theme.palette.blueGray200.main : undefined}
              onClick={handleEdit}
              disabled={isDeleting}
              size="thin"
            />
          </Box>

        </Box>

        {
          isDeleting &&
          <Stack 
            ref={deleteConfirmationRef}
            border={`2px solid ${theme.palette.pink.main}`}
            borderRadius="10px"
            sx={{ padding: "10px 20px 20px 20px", width: "100%" }}
            spacing={1.2}
            mt={2}
          >
            <Typography fontSize={28} color="pink" fontWeight={700}>
              {t("activityPlan.important")}
            </Typography>
            <Typography fontSize={16} color="oxfordBlue">
              {t("activityPlan.delete_warning")}
            </Typography>
            <Typography fontSize={16} color="primary" fontWeight={600}>
              {t("activityPlan.confirm_proceed")}
            </Typography>
            <InvestigationButton
              label={t("activityPlan.cancel")}
              fullWidth
              variant="main"
              outlined
              onClick={() => setIsDeleting(false)}
            />
            <InvestigationButton
              label={t("activityPlan.confirm")}
              variant="main"
              fullWidth
              onClick={handleDelete}
            />
          </Stack>
        }
      </Box>
    </DrawerContainer>
  )
};

const Resource = ({ resource }) => {

  const [t] = useTranslation("investigation");

  const { functions: { getResourceLabel } } = useConstants();

  return (
    <InvestigationCollapsable
      title={
        <Typography color="secondary.main" fontSize={14} fontWeight={600}>
          {resource.type === "access" 
          ? t("activityPlan.access_resource") 
          : resource.type === "financial" 
            ? t("activityPlan.financial_resource") 
            : resource.type === "material" 
              ? t("activityPlan.material_resource")
              : t("")}
          :&nbsp;
          <Typography component="span" fontSize={16} fontWeight={700}>
            {getResourceLabel(resource)}
          </Typography>
        </Typography>
      }
      details={ resource.details &&
        <Typography color="blueGray600.main" fontSize={11} fontWeight={600}>
          {t("activityPlan.answer")}:&nbsp;
          <Typography color="oxfordBlue.main" component="span" fontSize={11} fontStyle="italic">
            {resource.details}
          </Typography>
        </Typography>
      }
    /> 
  )

}



const Todo = ({ todo, idxTodo, isSavingTodos, handleToggleTodos }) => {

  const [t] = useTranslation("investigation");
  const theme = useTheme();
  const usersOpts = useSelector((state) => state.jaguar.currentReport.users.formatted);

  const { plans: { statusTodosOpts } } = useConstants();

  const [completed, setCompleted] = useState(todo.completed ?? false );
  const [showStatus, setShowStatus] = useState(false);

  const dateDeadline = useMemo(() => 
    formatDateAndTime(todo.deadline, todo.timeDeadline, t("Sin asignar")),
    [todo.deadline, todo.timeDeadline]
  );

  useEffect(() => {
    if (showStatus) {
      setCompleted(todo.completed ?? false);
    }
  }, [todo.completed, showStatus]);

  const handleSaveStatus = () => {
    let newTodo;
    if (typeof todo === "string") {
      newTodo = {
        ..._.cloneDeep(todoSchema),
        name: todo,
        completed,
        party: [],
      }
    } else {
      newTodo = {
        ...todo,
        completed,
      }
    }
    handleToggleTodos(idxTodo, newTodo, () => setShowStatus(false));
  }

  return (
    <InvestigationCollapsable
      title={
        <Typography color="secondary.main" fontSize={14}>
          {t("activityPlan.task")} {todo.completed ? t("activityPlan.completed") : t("activityPlan.pending")}:&nbsp;
          <Typography component="span" fontSize={16} fontWeight={700}>
            {typeof todo === "string" ? todo : todo.name}
          </Typography>
        </Typography>
      }
      details={(todo.assignedTo?.id || todo.details || todo.deadline || todo.dedicatedHours || todo.party?.length > 0) && <DetailsTodo todo={todo} />}
      secondCollapse={
        <>
        <div className="hr-div" >.</div>
        <Stack direction="row" spacing={1}>
          <EGBasicSelect 
            options={statusTodosOpts}
            value={completed}
            onChange={(e) => setCompleted(e.target.value)}
          />
          <Stack direction="row" spacing={.2} alignItems="center">
            <Box disabled={isSavingTodos}>
              <Icon
                name="bold_close_circle"
                size={24}
                color={theme.palette.error.main}
                style={{
                  cursor: 'pointer',
                  opacity: isSavingTodos ? 0.5 : 1,
                }}
                onClick={isSavingTodos ? () => {} : () => setShowStatus(false)}
              />
            </Box>
            <Box disabled={ isSavingTodos || completed === todo.completed }>
              <Icon
                name="bold_tick_circle"
                size={24}
                color={theme.palette.success.main}
                style={{
                  cursor: 'pointer',
                  opacity: isSavingTodos || completed === todo.completed ? 0.5 : 1,
                }}
                onClick={isSavingTodos || completed === todo.completed ? () => {} : handleSaveStatus}
              />
            </Box>
          </Stack>
        </Stack>
        </>
      }
      showSecondCollapse={showStatus}
      buttons={[
        {
          name: "outline_task_square",
          onClick: () => setShowStatus(!showStatus),
          color: theme.palette.primary.main,
          size: 16,
        },
      ]}
    /> 
  )
  

}